export const konekoAAdminUsernames = ["liam_maneki"];

/** Django `user_is_koneko_budget_admin` — org month-budget GET/PUT. */
export const konekoABudgetAdminUsernames = [
  "liam_maneki",
  "owen",
  "martin_ogg",
];

/** Sidebar “Koneko access” + `/koneko-a/access` — Liam only until roles ship. */
export const konekoAAccessControlUsernames = ["liam_maneki"];

/** When true, My budget / org editor read/write NekoHR API (see konekoABudgetBackendAllowlist). */
export const konekoABudgetBackendEnabled = true;

/**
 * Empty = all authenticated users. Non-empty = canonical usernames only (rollout phase 1).
 * Team rollout: keep [] so everyone uses the API after deploy.
 */
export const konekoABudgetBackendAllowlist = [];

/** From this calendar day onward, default month picker opens on next month (submission window). */
export const konekoABudgetSubmissionOpensDay = 20;

/** In-memory API cache: monthKey -> { username: { project: { low, high } } } */
export const konekoMemberTargetsApiCacheByMonth = {};

/** In-memory API cache: monthKey -> { project: { low, high, target? } } */
export const konekoOrgBudgetsApiCacheByMonth = {};

/** Today’s calendar month as `YYYY-MM` (default for Koneko month picker). */
export function currentKonekoCalendarMonthKey(now = new Date()) {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Default view month — always the current calendar month. */
export function defaultKonekoCalendarMonthKey(now = new Date()) {
  return currentKonekoCalendarMonthKey(now);
}

/** Display title, e.g. `May 2025`. */
export function formatKonekoMonthDisplayTitle(monthKey, locale = "en-GB") {
  const parsed = parseKonekoMonthKey(monthKey);
  if (!parsed) return String(monthKey || "").trim();
  const d = new Date(parsed.year, parsed.month - 1, 1);
  return d.toLocaleDateString(locale, {month: "long", year: "numeric"});
}

/** Add or subtract whole months from a `YYYY-MM` key. */
export function shiftKonekoMonthKey(monthKey, deltaMonths) {
  const parsed = parseKonekoMonthKey(monthKey);
  if (!parsed || !Number.isFinite(deltaMonths)) return monthKey;
  const d = new Date(parsed.year, parsed.month - 1 + deltaMonths, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

/** Parse `YYYY-MM` or return null. */
export function parseKonekoMonthKey(monthKey) {
  const m = String(monthKey || "").match(/^(\d{4})-(\d{2})$/);
  if (!m) return null;
  const year = parseInt(m[1], 10);
  const month = parseInt(m[2], 10);
  if (
    !Number.isFinite(year) ||
    !Number.isFinite(month) ||
    month < 1 ||
    month > 12
  ) {
    return null;
  }
  return {year, month};
}

/**
 * Month used for timesheet API + logged-hour totals — always the selected calendar month.
 * Future months (e.g. June while still in May) fetch that range and show zeros until data exists.
 */
export function effectiveTimesheetMonthKey(calendarMonthKey, now = new Date()) {
  if (parseKonekoMonthKey(calendarMonthKey)) return calendarMonthKey;
  return currentKonekoCalendarMonthKey(now);
}

/** True when `YYYY-MM` is after the current calendar month. */
export function isFutureKonekoCalendarMonthKey(
  calendarMonthKey,
  now = new Date()
) {
  const parsed = parseKonekoMonthKey(calendarMonthKey);
  if (!parsed) return false;
  const selectedStart = new Date(parsed.year, parsed.month - 1, 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return selectedStart > today;
}

/**
 * Personal monthly caps: member username (lowercase, match API) -> project -> "H:MM".
 * Imported from spreadsheets (sheet said April; IDs here follow May calendar key via byMonth merge).
 * All project keys quoted so ":" / "!" in names stay valid JS.
 */
export const konekoATargets = {
  liam_maneki: {
    "Atlantis Business Support": {low: "0:00", high: "10:00"},
    "Mission Catastrophic!": {low: "0:00", high: "5:00"},
    "CRAZIA: Avatar": {low: "0:00", high: "3:00"},
    Koneko: {low: "0:00", high: "5:00"},
    NekoAdmin: {low: "0:00", high: "20:00"},
    NekoBizDev: {low: "0:00", high: "25:00"},
    NekoHR: {low: "0:00", high: "10:00"},
    "NekoLogic Website": {low: "0:00", high: "5:00"},
    Rhythmscapes: {low: "0:00", high: "5:00"},
  },
  reegesh: {
    "CRAZIA: Avatar": {low: "0:00", high: "40:00"},
    Koneko: {low: "0:00", high: "10:00"},
    Rhythmscapes: {low: "0:00", high: "1:00"},
    "Questy Chess": {low: "0:00", high: "1:00"},
    NekoProduction: {low: "0:00", high: "1:00"},
  },
  victor: {
    Koneko: {low: "0:00", high: "35:00"},
    NekoAdmin: {low: "0:00", high: "30:00"},
    NekoProduction: {low: "0:00", high: "45:00"},
  },
  kai: {
    "Mission Catastrophic!": {low: "0:00", high: "10:00"},
    Koneko: {low: "0:00", high: "10:00"},
    NekoAdmin: {low: "0:00", high: "30:00"},
    NekoBizDev: {low: "0:00", high: "30:00"},
    NekoProduction: {low: "0:00", high: "20:00"},
    "Nekologic Intranet": {low: "0:00", high: "10:00"},
    "Questy Chess": {low: "0:00", high: "30:00"},
    Rhythmscapes: {low: "0:00", high: "10:00"},
  },
  martin_ogg: {
    Koneko: {low: "0:00", high: "50:00"},
    NekoAdmin: {low: "0:00", high: "10:00"},
    AnSo: {low: "0:00", high: "20:00"},
  },
  florian: {
    "CRAZIA: Avatar": {low: "0:00", high: "30:00"},
    "CRAZIA: Moderation": {low: "0:00", high: "30:00"},
    "Mission Catastrophic!": {low: "0:00", high: "30:00"},
    Rhythmscapes: {low: "0:00", high: "30:00"},
  },
  sylvio: {
    "Mission Catastrophic!": {low: "0:00", high: "30:00"},
    "Questy Chess": {low: "0:00", high: "60:00"},
    NekoProduction: {low: "0:00", high: "30:00"},
  },
  tokiko: {
    "Atlantis Business Support": {low: "0:00", high: "15:00"},
    NekoAdmin: {low: "0:00", high: "47:00"},
    NekoHR: {low: "0:00", high: "5:00"},
  },
  marisa: {
    NekoAdmin: {low: "10:00", high: "50:00"},
    NekoHR: {low: "0:00", high: "6:00"},
    Shinnyadou: {low: "0:00", high: "4:00"},
  },
  sebastian: {
    "Mission Catastrophic!": {low: "0:00", high: "90:00"},
    NekoBizDev: {low: "0:00", high: "10:00"},
  },
  ben: {
    "Mission Catastrophic!": {low: "0:00", high: "80:00"},
    Koneko: {low: "0:00", high: "10:00"},
    NekoAdmin: {low: "0:00", high: "20:00"},
    NekoBizDev: {low: "0:00", high: "10:00"},
    Rhythmscapes: {low: "0:00", high: "40:00"},
  },
  pablo: {
    AnSo: {low: "0:00", high: "30:00"},
    NekoBizDev: {low: "0:00", high: "20:00"},
    "NekoLogic Website": {low: "0:00", high: "20:00"},
    "Umami Sans Frontieres": {low: "0:00", high: "30:00"},
  },
  douglas: {
    "CRAZIA: Moderation": {low: "0:00", high: "110:00"},
    "Firefighter 3D": {low: "0:00", high: "20:00"},
  },
  chihiro: {
    Shinnyadou: {low: "0:00", high: "70:00"},
  },
  owen: {
    AnSo: {low: "0:00", high: "20:00"},
    "Yodobashi Components": {low: "0:00", high: "130:00"},
  },
  jacques: {
    "Mission Catastrophic!": {low: "0:00", high: "10:00"},
    Koneko: {low: "0:00", high: "40:00"},
    NekoProduction: {low: "0:00", high: "10:00"},
    Rhythmscapes: {low: "0:00", high: "50:00"},
    "Questy Chess": {low: "0:00", high: "1:00"},
    "CRAZIA: Avatar": {low: "0:00", high: "50:00"},
  },
  abdel: {
    AnSo: {low: "0:00", high: "60:00"},
    Koneko: {low: "0:00", high: "60:00"},
    "Mission Catastrophic!": {low: "0:00", high: "40:00"},
  },
};

function deepCloneTargets(targets) {
  return JSON.parse(JSON.stringify(targets || {}));
}

/**
 * Optional per-month overrides (`YYYY-MM`) merged on top of `konekoATargets`.
 * Use when May (or another month) differs from the base map above.
 */
function june2026TargetsWithoutDepartingMembers() {
  const t = deepCloneTargets(konekoATargets);
  delete t.owen;
  delete t.abdel;
  return t;
}

export const konekoATargetsByMonth = {
  // Partial overrides merge on top of `konekoATargets`, e.g.
  // "2026-05": { liam_maneki: { "NekoHR": "12:00" } },
  /** Full roster for June (not a delta) — Owen + Adam (abdel) omitted. */
  "2026-06": june2026TargetsWithoutDepartingMembers(),
};

/**
 * Months where `konekoATargetsByMonth[YYYY-MM]` is the complete roster for that month
 * (do not merge `konekoATargets` base back in — that would resurrect departing members).
 */
export const konekoAMonthFullRosterReplaceMonths = new Set(["2026-06"]);

/** First calendar month (YYYY-MM) with no personal-budget UI for listed roster keys. */
export const konekoAPersonalBudgetExcludedFromMonth = "2026-06";

/** Roster keys (canonical) excluded from personal budget from `konekoAPersonalBudgetExcludedFromMonth`. */
export const konekoAPersonalBudgetExcludedMemberKeys = ["owen", "abdel"];

function rosterRowUsername(row) {
  if (!row || typeof row !== "object") return "";
  const nested = row.user;
  if (nested && typeof nested === "object" && !Array.isArray(nested)) {
    return String(nested.username || "").trim();
  }
  return String(row.username || row.user_name || "").trim();
}

/**
 * Map canonical / planning roster keys → { is_currently_working, koneko_excluded_from_month }.
 * Built from `api/users/team-members/` (admin may pass include_not_working=1).
 */
export function buildKonekoTeamRosterMetaByUsername(teamRows = []) {
  const out = {};
  const attach = (key, row) => {
    const canon = canonicalizeKonekoUsername(key);
    if (!canon) return;
    out[canon] = {
      is_currently_working: row.is_currently_working !== false,
      koneko_excluded_from_month: row.koneko_excluded_from_month || null,
      full_name: row.full_name || "",
    };
    const planning = resolveKonekoAPlanningUsernameKey(key);
    if (planning && planning !== canon) {
      out[planning] = out[canon];
    }
  };
  (teamRows || []).forEach((row) => {
    const uname = rosterRowUsername(row);
    if (uname) attach(uname, row);
    if (row.full_name) attach(row.full_name, row);
  });
  return out;
}

/**
 * Hide member from Koneko A for `monthKey` when:
 * - static config lists them from `konekoAPersonalBudgetExcludedFromMonth`, or
 * - backend `Member.koneko_excluded_from_month` <= month (departing, still “working”).
 */
export function isKonekoMemberExcludedForMonth(
  monthKey,
  memberKey,
  rosterMetaByUsername = null,
  displayName = ""
) {
  const mk = String(monthKey || "").trim();
  if (!mk) return false;
  const candidates = usernameCandidates(memberKey, displayName);
  for (const key of candidates) {
    const canon = canonicalizeKonekoUsername(key);
    if (!canon) continue;
    const staticFrom = String(
      konekoAPersonalBudgetExcludedFromMonth || ""
    ).trim();
    if (
      staticFrom &&
      mk >= staticFrom &&
      (konekoAPersonalBudgetExcludedMemberKeys || []).some(
        (k) => canonicalizeKonekoUsername(k) === canon
      )
    ) {
      return true;
    }
    const meta =
      rosterMetaByUsername &&
      (rosterMetaByUsername[canon] || rosterMetaByUsername[key]);
    const departFrom =
      meta && String(meta.koneko_excluded_from_month || "").trim();
    if (departFrom && mk >= departFrom) return true;
  }
  return false;
}

/** @deprecated Use isKonekoMemberExcludedForMonth — kept for call sites. */
export function isKonekoMemberExcludedFromPersonalBudget(
  monthKey,
  memberKey,
  rosterMetaByUsername = null,
  displayName = ""
) {
  return isKonekoMemberExcludedForMonth(
    monthKey,
    memberKey,
    rosterMetaByUsername,
    displayName
  );
}

export function listKonekoAPlanningMemberKeysForBudget(
  monthKey,
  rosterMetaByUsername = null
) {
  return listKonekoAPlanningMemberKeys().filter(
    (k) => !isKonekoMemberExcludedForMonth(monthKey, k, rosterMetaByUsername)
  );
}

/**
 * Username aliases -> canonical config keys.
 * Add entries as you confirm exact usernames from team-members endpoint.
 */
export const konekoAUsernameAliases = {
  liam: "liam_maneki",
  "liam.maneki": "liam_maneki",
  // Keep Reegesh canonicalized to the configured key in konekoATargets.
  reegesh: "reegesh",
  regesh: "reegesh",
  rishek: "rishek",
  // Do not map bare "adam" → abdel (collision with other Adams). Use explicit forms.
  "adam abdel": "abdel",
  "adam.abdel": "abdel",
  "adam eba": "abdel",
  adam_eba: "abdel",
  "owen bennett": "owen",
  owen_bennett: "owen",
  reegesh_maharjan: "reegesh",
  "reegesh.maharjan": "reegesh",
  "reegesh-maharjan": "reegesh",
  reegeshmaharjan: "reegesh",
  regesh_maharjan: "reegesh",
  "regesh.maharjan": "reegesh",
  regeshmaharjan: "reegesh",
  "reegesh maharjan": "reegesh",
  "regesh maharjan": "reegesh",
  "douglas@neko-logic.com": "douglas",
  "kiho@neko-logic.com": "kiho",
};

/**
 * Members with zero month hours who should show “Reporting Elsewhere” (lowercase username).
 */
export const konekoReportingElsewhereUsernames = [];

function normalizeUserLookupKey(username) {
  return String(username || "")
    .trim()
    .toLowerCase()
    .normalize("NFKC");
}

export function canonicalizeKonekoUsername(username) {
  const raw = normalizeUserLookupKey(username);
  if (!raw) return "";
  if (konekoAUsernameAliases[raw]) return konekoAUsernameAliases[raw];
  const localPart = raw.includes("@") ? raw.split("@")[0] : raw;
  if (konekoAUsernameAliases[localPart])
    return konekoAUsernameAliases[localPart];
  return localPart;
}

export function isKonekoAccessControlUser(profile) {
  const key = canonicalizeKonekoUsername(profile && profile.username);
  if (!key) return false;
  return konekoAAccessControlUsernames.some(
    (a) => String(a).toLowerCase() === key
  );
}

/** Resolve login/JWT profile fields to a roster username key. */
export function profileUsernameFromJwt(profile) {
  if (!profile || typeof profile !== "object") return "";
  const candidates = [
    profile.username,
    profile.user_username,
    profile.user_name,
    profile.email,
  ];
  for (const raw of candidates) {
    const s = String(raw || "").trim();
    if (!s) continue;
    const canon = canonicalizeKonekoUsername(s);
    if (canon) return canon;
  }
  return "";
}

export function profileDisplayNameFromJwt(profile) {
  if (!profile || typeof profile !== "object") return "";
  return String(
    profile.full_name || profile.name || profile.username || ""
  ).trim();
}

/** Same roster Koneko A uses for budgeting: targets + reporting-elsewhere-only members. */
export function listKonekoAPlanningMemberKeys() {
  const set = new Set(Object.keys(konekoATargets || {}));
  (konekoReportingElsewhereUsernames || []).forEach((u) => {
    const x = String(u || "")
      .trim()
      .toLowerCase();
    if (x) set.add(x);
  });
  return Array.from(set).sort((a, b) =>
    a.replace(/_/g, " ").localeCompare(b.replace(/_/g, " "), undefined, {
      sensitivity: "base",
    })
  );
}

/** Optional prettier labels for Dashboard "View as" (value stays roster key, e.g. abdel). */
export const konekoAPlanningMemberOptionLabels = {
  reegesh: "Reegesh",
  abdel: "Adam Eba",
};

export function formatKonekoAPlanningMemberSelectLabel(key) {
  if (!key) return "";
  if (konekoAPlanningMemberOptionLabels[key]) {
    return konekoAPlanningMemberOptionLabels[key];
  }
  return key
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}

let planningUsernameLookupMap = null;
const planningUsernameKeyCache = new Map();

function buildPlanningUsernameLookupMap() {
  const map = new Map();
  const keys = listKonekoAPlanningMemberKeys();
  for (const k of keys) {
    map.set(normalizeUserLookupKey(k), k);
    const canon = canonicalizeKonekoUsername(k);
    if (canon) map.set(normalizeUserLookupKey(canon), k);
    const label = formatKonekoAPlanningMemberSelectLabel(k);
    if (label) map.set(normalizeUserLookupKey(label), k);
  }
  return map;
}

function planningUsernameLookup() {
  if (!planningUsernameLookupMap) {
    planningUsernameLookupMap = buildPlanningUsernameLookupMap();
  }
  return planningUsernameLookupMap;
}

/** Map pasted or legacy-stored username to a key from `listKonekoAPlanningMemberKeys()`, or "". */
export function resolveKonekoAPlanningUsernameKey(usernameInput) {
  const t = usernameInput == null ? "" : String(usernameInput).trim();
  if (!t) return "";
  const cacheKey = normalizeUserLookupKey(t);
  if (!cacheKey) return "";
  if (planningUsernameKeyCache.has(cacheKey)) {
    return planningUsernameKeyCache.get(cacheKey);
  }
  const keys = listKonekoAPlanningMemberKeys();
  const allowed = new Set(keys);
  let result = "";
  if (allowed.has(t)) {
    result = t;
  } else {
    const canon = canonicalizeKonekoUsername(t);
    if (canon && allowed.has(canon)) {
      result = canon;
    } else {
      for (const k of keys) {
        if (canonicalizeKonekoUsername(k) === canon) {
          result = k;
          break;
        }
      }
      if (!result) {
        result = planningUsernameLookup().get(cacheKey) || "";
      }
    }
  }
  planningUsernameKeyCache.set(cacheKey, result);
  return result;
}

function usernameCandidates(username, displayName = "") {
  const out = [];
  const pushCandidate = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return;
    const canonical = canonicalizeKonekoUsername(raw);
    if (canonical && !out.includes(canonical)) out.push(canonical);
    // Keep normalized raw variant too (useful for direct object keys that are already canonical).
    const normalizedRaw = normalizeUserLookupKey(raw);
    if (normalizedRaw && !out.includes(normalizedRaw)) out.push(normalizedRaw);
  };

  const canonical = canonicalizeKonekoUsername(username);
  pushCandidate(canonical);
  const simpleToken = canonical.split(/[._\-\s]/)[0];
  pushCandidate(simpleToken);

  const nameRaw = normalizeUserLookupKey(displayName);
  if (nameRaw) {
    const nameParts = nameRaw.split(/\s+/).filter(Boolean);
    if (nameParts.length) {
      const first = nameParts[0];
      const last = nameParts[nameParts.length - 1];
      pushCandidate(first);
      pushCandidate(last);
      const joinedUnderscore = nameParts.join("_");
      pushCandidate(joinedUnderscore);
      const joinedDot = nameParts.join(".");
      pushCandidate(joinedDot);
    }
  }
  const planning =
    resolveKonekoAPlanningUsernameKey(username) ||
    resolveKonekoAPlanningUsernameKey(displayName);
  if (planning && !out.includes(planning)) out.push(planning);
  return out;
}

/**
 * True when a team-member row is the same person as the logged-in user, View-as
 * selection, or `?member=` query. Uses the same username/name expansion as
 * `resolveMemberTargets` so JWT vs team-list naming matches the main Koneko A view.
 */
export function konekoMemberRowMatchesLookup(
  memberUsername,
  memberName,
  lookupUsername,
  lookupDisplayName = ""
) {
  if (
    !String(lookupUsername || "").trim() &&
    !String(lookupDisplayName || "").trim()
  ) {
    return false;
  }
  const canonRow = canonicalizeKonekoUsername(memberUsername);
  const canonLookup = canonicalizeKonekoUsername(lookupUsername);
  if (canonRow && canonLookup && canonRow === canonLookup) {
    return true;
  }
  const rowCands = usernameCandidates(memberUsername, memberName);
  const lookupCands = usernameCandidates(lookupUsername, lookupDisplayName);
  for (const lc of lookupCands) {
    const cLc = canonicalizeKonekoUsername(lc);
    if (!cLc) continue;
    for (const rc of rowCands) {
      const cRc = canonicalizeKonekoUsername(rc);
      if (cRc && cLc === cRc) return true;
    }
  }
  return false;
}

/**
 * Match a planning roster key (View-as / ?member=) to API `username` only — no display names.
 * Avoids first-name collisions (e.g. “Adam …” vs Adam Eba keyed as abdel).
 */
export function konekoPlanningKeyMatchesMemberRow(memberUsername, planningKey) {
  const pk = normalizeUserLookupKey(planningKey);
  const mu = normalizeUserLookupKey(memberUsername);
  if (!pk || !mu) return false;
  if (mu === pk) return true;
  const cmu = canonicalizeKonekoUsername(memberUsername);
  const cpk = canonicalizeKonekoUsername(planningKey);
  if (cmu && cpk && cmu === cpk) return true;
  const firstSeg = mu.split(/[-._]/)[0];
  if (firstSeg && firstSeg === pk) return true;
  if (
    mu.startsWith(`${pk}_`) ||
    mu.startsWith(`${pk}.`) ||
    mu.startsWith(`${pk}-`)
  ) {
    return true;
  }
  const cpkCanon = canonicalizeKonekoUsername(planningKey);
  if (cpkCanon) {
    for (const [aliasKey, targetCanon] of Object.entries(
      konekoAUsernameAliases
    )) {
      if (targetCanon !== cpkCanon) continue;
      const ak = normalizeUserLookupKey(aliasKey);
      if (!ak) continue;
      if (mu === ak) return true;
      if (
        mu.startsWith(`${ak}_`) ||
        mu.startsWith(`${ak}.`) ||
        mu.startsWith(`${ak}-`)
      ) {
        return true;
      }
    }
  }
  return false;
}

export function normalizeTargetBandForEditor(raw) {
  if (raw == null || raw === "") return {low: "", high: ""};
  if (typeof raw === "string") {
    return {low: "", high: normalizeBudgetHhmmInput(String(raw).trim())};
  }
  if (typeof raw === "number") return {low: "", high: `${raw}:00`};
  if (typeof raw === "object") {
    return {
      low: normalizeBudgetHhmmInput(raw.low ?? raw.min ?? ""),
      high: normalizeBudgetHhmmInput(raw.high ?? raw.max ?? raw.target ?? ""),
    };
  }
  return {low: "", high: ""};
}

/**
 * Budget min/max fields: bare whole hours (e.g. `15`) → `15:00`.
 * Existing `H:MM` / `HH:MM` values are kept (minutes zero-padded).
 */
export function normalizeBudgetHhmmInput(value) {
  if (value == null || value === "") return "";
  const s = String(value).trim();
  if (!s) return "";
  const hm = s.match(/^(\d+)\s*:\s*(\d{1,2})$/);
  if (hm) {
    const h = parseInt(hm[1], 10);
    const mm = Math.min(59, Math.max(0, parseInt(hm[2], 10) || 0));
    return Number.isFinite(h) ? `${h}:${String(mm).padStart(2, "0")}` : s;
  }
  if (/^\d+$/.test(s)) {
    return `${parseInt(s, 10)}:00`;
  }
  return s;
}

/** Normalize `{ project: { low, high, target? } }` before save/API. */
export function normalizeBudgetProjectMap(map) {
  if (!map || typeof map !== "object") return {};
  const out = {};
  Object.keys(map).forEach((project) => {
    const raw = map[project];
    if (raw == null || raw === "") {
      out[project] = {low: "", high: ""};
      return;
    }
    if (typeof raw === "string" || typeof raw === "number") {
      out[project] = normalizeTargetBandForEditor(raw);
      return;
    }
    if (typeof raw === "object") {
      const band = {
        low: normalizeBudgetHhmmInput(raw.low ?? raw.min ?? ""),
        high: normalizeBudgetHhmmInput(raw.high ?? raw.max ?? raw.target ?? ""),
      };
      if (raw.target != null && raw.target !== "") {
        band.target = normalizeBudgetHhmmInput(raw.target);
      }
      out[project] = band;
      return;
    }
    out[project] = {low: "", high: ""};
  });
  return out;
}

/** Stable localStorage identity for personal target drafts (canonical username). */
export function memberTargetsDraftStorageUsername(username, displayName = "") {
  for (const key of usernameCandidates(username, displayName)) {
    const canon = canonicalizeKonekoUsername(key);
    if (canon) return canon;
  }
  const canon = canonicalizeKonekoUsername(username);
  if (canon) return canon;
  const first = usernameCandidates(username, displayName)[0];
  return first ? normalizeUserLookupKey(first) : "";
}

export function memberTargetsDraftStorageKey(
  monthKey,
  username,
  displayName = ""
) {
  const mk = String(monthKey || "").trim();
  const who = memberTargetsDraftStorageUsername(username, displayName);
  if (!mk || !who) return "";
  return `konekoA:memberTargetsDraft:${mk}:${who}`;
}

/** Browser-only personal caps for a month (`project` → `{ low, high }`). */
export function readMemberTargetsLocalDraft(
  monthKey,
  username,
  displayName = ""
) {
  const storageKey = memberTargetsDraftStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return {};
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return {};
    }
    const out = {};
    Object.keys(parsed).forEach((project) => {
      out[project] = normalizeTargetBandForEditor(parsed[project]);
    });
    return out;
  } catch {
    return {};
  }
}

export function writeMemberTargetsLocalDraft(
  monthKey,
  username,
  displayName,
  draft
) {
  const storageKey = memberTargetsDraftStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(draft || {}));
  } catch {
    // ignore quota errors
  }
}

export function clearMemberTargetsLocalDraft(monthKey, username, displayName) {
  const storageKey = memberTargetsDraftStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

function memberTargetsHiddenStorageKey(monthKey, username, displayName = "") {
  const mk = String(monthKey || "").trim();
  const who = memberTargetsDraftStorageUsername(username, displayName);
  if (!mk || !who) return "";
  return `konekoA:memberTargetsHidden:${mk}:${who}`;
}

/** Project names hidden from My budget for this month (still may have timesheet time). */
export function readMemberTargetsHiddenProjects(
  monthKey,
  username,
  displayName = ""
) {
  const storageKey = memberTargetsHiddenStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return [];
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((p) => String(p || "").trim()).filter(Boolean);
  } catch {
    return [];
  }
}

export function writeMemberTargetsHiddenProjects(
  monthKey,
  username,
  displayName,
  projectNames
) {
  const storageKey = memberTargetsHiddenStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return;
  const list = Array.isArray(projectNames)
    ? projectNames.map((p) => String(p || "").trim()).filter(Boolean)
    : [];
  try {
    if (!list.length) {
      localStorage.removeItem(storageKey);
    } else {
      localStorage.setItem(storageKey, JSON.stringify(list));
    }
  } catch {
    // ignore quota errors
  }
}

export function clearMemberTargetsHiddenProjects(
  monthKey,
  username,
  displayName
) {
  const storageKey = memberTargetsHiddenStorageKey(
    monthKey,
    username,
    displayName
  );
  if (!storageKey) return;
  try {
    localStorage.removeItem(storageKey);
  } catch {
    // ignore
  }
}

/**
 * Config-only targets (repo + `konekoATargetsByMonth`), without browser drafts.
 */
export function resolveMemberTargetsFromConfig(
  monthKey,
  username,
  displayName = "",
  rosterMetaByUsername = null
) {
  if (
    isKonekoMemberExcludedForMonth(
      monthKey,
      username,
      rosterMetaByUsername,
      displayName
    )
  ) {
    return {};
  }
  const candidates = usernameCandidates(username, displayName);
  const mk = String(monthKey || "").trim();
  const monthBucket =
    mk && typeof konekoATargetsByMonth[mk] === "object"
      ? konekoATargetsByMonth[mk]
      : null;
  const pickObject = (bucket) => {
    if (!bucket || typeof bucket !== "object") return {};
    for (const key of candidates) {
      if (bucket[key] && typeof bucket[key] === "object") return bucket[key];
    }
    return {};
  };
  const fromMonth = pickObject(monthBucket);
  const fromBase = pickObject(konekoATargets);
  const fullRosterMonth = mk && konekoAMonthFullRosterReplaceMonths.has(mk);
  const merged = fullRosterMonth ? fromMonth : {...fromBase, ...fromMonth};
  if (fullRosterMonth && !Object.keys(merged).length) {
    return {};
  }
  const out = {};
  Object.keys(merged).forEach((project) => {
    out[project] = normalizeTargetBandForEditor(merged[project]);
  });
  return out;
}

export function isKonekoBudgetBackendEnabledForUser(username) {
  if (!konekoABudgetBackendEnabled) return false;
  const list = konekoABudgetBackendAllowlist || [];
  if (!list.length) return true;
  const canon = canonicalizeKonekoUsername(username);
  return list.some((entry) => canonicalizeKonekoUsername(entry) === canon);
}

/** True when this member has rows saved in NekoHR for the month (not config-only). */
export function memberTargetsSavedInApi(
  monthKey,
  memberKey,
  apiCacheByMonth = konekoMemberTargetsApiCacheByMonth,
  displayName = ""
) {
  const targets = rosterTargetsFromApiCache(
    monthKey,
    memberKey,
    displayName,
    apiCacheByMonth
  );
  return (
    targets && typeof targets === "object" && Object.keys(targets).length > 0
  );
}

function ordinalWord(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num < 1) return `${n}th`;
  const mod100 = num % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${num}th`;
  const mod10 = num % 10;
  if (mod10 === 1) return `${num}st`;
  if (mod10 === 2) return `${num}nd`;
  if (mod10 === 3) return `${num}rd`;
  return `${num}th`;
}

function formatSubmissionTimestamp(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Human label for roster submission state (admin chase-up + member confirmation). */
export function formatKonekoSubmissionLabel(submission) {
  const count = submission && submission.count ? Number(submission.count) : 0;
  if (!count) return "Not submitted";
  const when = formatSubmissionTimestamp(
    submission.lastSubmittedAt || submission.firstSubmittedAt
  );
  if (count === 1) {
    return when ? `Submitted on ${when}` : "Submitted";
  }
  return when
    ? `${ordinalWord(count)} submission on ${when}`
    : `${ordinalWord(count)} submission`;
}

export function submissionFromCache(
  monthKey,
  memberKey,
  submissionsCacheByMonth = {},
  displayName = ""
) {
  const bucket = submissionsCacheByMonth[monthKey];
  if (!bucket || typeof bucket !== "object") {
    return {count: 0, firstSubmittedAt: null, lastSubmittedAt: null};
  }
  const candidates = usernameCandidates(memberKey, displayName);
  for (const key of candidates) {
    const canon = canonicalizeKonekoUsername(key);
    if (canon && bucket[canon]) return bucket[canon];
    if (key && bucket[key]) return bucket[key];
  }
  const canon = canonicalizeKonekoUsername(memberKey);
  for (const [storedUser, row] of Object.entries(bucket)) {
    for (const key of candidates) {
      if (
        canonicalizeKonekoUsername(storedUser) ===
        canonicalizeKonekoUsername(key)
      ) {
        return row;
      }
    }
    if (canonicalizeKonekoUsername(storedUser) === canon) return row;
  }
  return {count: 0, firstSubmittedAt: null, lastSubmittedAt: null};
}

function rosterTargetsFromApiCache(
  monthKey,
  username,
  displayName = "",
  apiCacheByMonth = konekoMemberTargetsApiCacheByMonth
) {
  const bucket = apiCacheByMonth[monthKey];
  if (!bucket || typeof bucket !== "object") return null;
  const candidates = usernameCandidates(username, displayName);
  for (const key of candidates) {
    const canon = canonicalizeKonekoUsername(key);
    if (canon && bucket[canon]) return bucket[canon];
    if (key && bucket[key]) return bucket[key];
  }
  for (const [storedUser, targets] of Object.entries(bucket)) {
    for (const key of candidates) {
      if (
        canonicalizeKonekoUsername(storedUser) ===
        canonicalizeKonekoUsername(key)
      ) {
        return targets;
      }
    }
  }
  return null;
}

/**
 * Target map after merging base + monthly overrides (`YYYY-MM`) + local drafts.
 */
export function resolveMemberTargets(
  monthKey,
  username,
  displayName = "",
  apiCacheByMonth = konekoMemberTargetsApiCacheByMonth,
  rosterMetaByUsername = null
) {
  const planningKey =
    resolveKonekoAPlanningUsernameKey(username) ||
    resolveKonekoAPlanningUsernameKey(displayName);
  if (
    isKonekoMemberExcludedForMonth(
      monthKey,
      username,
      rosterMetaByUsername,
      displayName
    ) ||
    (planningKey &&
      isKonekoMemberExcludedForMonth(
        monthKey,
        planningKey,
        rosterMetaByUsername,
        displayName
      ))
  ) {
    return {};
  }
  const fromConfig = resolveMemberTargetsFromConfig(
    monthKey,
    username,
    displayName,
    rosterMetaByUsername
  );
  const merged = {...fromConfig};
  const fromApi = rosterTargetsFromApiCache(
    monthKey,
    username,
    displayName,
    apiCacheByMonth
  );
  if (fromApi && typeof fromApi === "object") {
    Object.keys(fromApi).forEach((project) => {
      merged[project] = {
        ...normalizeTargetBandForEditor(merged[project]),
        ...normalizeTargetBandForEditor(fromApi[project]),
      };
    });
  }
  if (!isKonekoBudgetBackendEnabledForUser(username)) {
    const localDraft = readMemberTargetsLocalDraft(
      monthKey,
      username,
      displayName
    );
    Object.keys(localDraft).forEach((project) => {
      merged[project] = {
        ...normalizeTargetBandForEditor(merged[project]),
        ...normalizeTargetBandForEditor(localDraft[project]),
      };
    });
  }
  return merged;
}

function findLimitsValueForProject(section, projectName) {
  if (!section || typeof section !== "object") return null;
  const needle = normalizeBudgetLookupKey(projectName);
  if (!needle) return null;
  const keys = Object.keys(section);
  const exact = keys.find((k) => normalizeBudgetLookupKey(k) === needle);
  let pick = exact != null ? section[exact] : null;
  if (pick == null || pick === "") {
    const needleLower = needle.toLowerCase();
    const ci = keys.find(
      (k) => normalizeBudgetLookupKey(k).toLowerCase() === needleLower
    );
    pick = ci != null ? section[ci] : null;
  }
  if (pick == null || pick === "") {
    const needleLoose = normalizeBudgetLooseKey(needle);
    const loose = keys.find((k) => normalizeBudgetLooseKey(k) === needleLoose);
    pick = loose != null ? section[loose] : null;
  }
  if (pick == null || pick === "") {
    const canonNeedle = konekoProjectCanonKey(needle);
    const alias = keys.find((k) => konekoProjectCanonKey(k) === canonNeedle);
    pick = alias != null ? section[alias] : null;
  }
  if (pick == null || pick === "") return null;
  if (typeof pick === "string") return String(pick).trim();
  if (typeof pick === "number") return `${pick}:00`;
  if (typeof pick === "object") return pick;
  return null;
}

/** Person + project HH:MM cap from merged config (`null` = FLEX). */
export function getMemberProjectHHMMLimit(
  monthKey,
  username,
  projectName,
  displayName = "",
  apiCacheByMonth = konekoMemberTargetsApiCacheByMonth
) {
  const raw = findLimitsValueForProject(
    resolveMemberTargets(monthKey, username, displayName, apiCacheByMonth),
    projectName
  );
  if (raw == null) return null;
  if (typeof raw === "string") return raw;
  if (typeof raw === "object") {
    if (raw.target) return String(raw.target).trim();
    if (raw.high) return String(raw.high).trim();
    if (raw.low) return String(raw.low).trim();
    if (raw.max) return String(raw.max).trim();
    if (raw.min) return String(raw.min).trim();
  }
  return null;
}

/** Person + project band from member caps (`null` = FLEX). */
export function getMemberProjectLimitBand(
  monthKey,
  username,
  projectName,
  displayName = "",
  apiCacheByMonth = konekoMemberTargetsApiCacheByMonth,
  rosterMetaByUsername = null
) {
  const raw = findLimitsValueForProject(
    resolveMemberTargets(
      monthKey,
      username,
      displayName,
      apiCacheByMonth,
      rosterMetaByUsername
    ),
    projectName
  );
  if (raw == null) return null;
  if (typeof raw === "object") {
    return {
      // Support aliases so spreadsheet-style min/max does not fall back to FLEX.
      low: raw.low ?? raw.min ?? null,
      high: raw.high ?? raw.max ?? null,
      target: raw.target ?? null,
    };
  }
  if (typeof raw === "string") {
    return {low: null, high: raw, target: null};
  }
  return null;
}

/**
 * Org-wide project budget bands by calendar month (YYYY-MM).
 * Fill in each month you care about; unknown months / projects fall back to FLEX or summed member targets.
 *
 * Project keys must match API names (see “Project” column). Use quotes for names with special chars:
 *   "CRAZIA: Avatar": { low: "65:00", high: "85:00" },
 * Colons inside quoted keys do not conflict with HH:MM times — those only appear in low/high/target strings.
 *
 * Per project, use either:
 *   - `target` alone  → "NN% of budget"
 *   - `low` / `high`  → "NN% of low end of budget" / "high end" / "within budget band …"
 *   - only `low` or only `high` → compares against that bound only
 *
 * Example (HH:MM):
 *   NekoBizDev: { low: "65:00", high: "85:00" },
 */
export const konekoAMonthlyProjectBudgets = {
  "2026-05": {
    // Add / adjust projects to match API project names exactly.
    "CRAZIA: Moderation": {low: "140:00", high: "160:00"},
    Shinnyadou: {low: "00:00", high: "50:00"},
    "CRAZIA: Avatar": {low: "140:00", high: "160:00"},
    NekoAdmin: {low: "00:00", high: "150:00"},
    "Mission Catastrophic!": {low: "00:00", high: "450:00"},
    "Umami Sans Frontieres": {low: "00:00", high: "32:00"},
    "NekoLogic Website": {low: "25:00", high: "100:00"},
    AnSo: {low: "25:00", high: "125:00"},
    NekoHR: {low: "00:00", high: "75:00"},
    NekoProduction: {low: "00:00", high: "80:00"},
    NekoBizDev: {low: "00:00", high: "200:00"},
    "Yodobashi Components": {low: "00:00", high: "00:00"},
    "Hippokampe (Digirail)": {low: "00:00", high: "00:00"},
    NekoRecruit: {low: "00:00", high: "20:00"},
    Koneko: {low: "50:00", high: "200:00"},
    Rhythmscapes: {low: "50:00", high: "275:00"},
    "Questy Chess": {low: "00:00", high: "50:00"},
    "Atlantis Business Support": {low: "00:00", high: "32:00"},
  },
  // June baseline currently mirrors May until producer-adjusted figures arrive.
  "2026-06": {
    "CRAZIA: Moderation": {low: "140:00", high: "160:00"},
    Shinnyadou: {low: "00:00", high: "50:00"},
    "CRAZIA: Avatar": {low: "140:00", high: "160:00"},
    NekoAdmin: {low: "00:00", high: "150:00"},
    "Mission Catastrophic!": {low: "00:00", high: "450:00"},
    "Umami Sans Frontieres": {low: "00:00", high: "32:00"},
    "NekoLogic Website": {low: "25:00", high: "100:00"},
    AnSo: {low: "25:00", high: "125:00"},
    NekoHR: {low: "00:00", high: "75:00"},
    NekoProduction: {low: "00:00", high: "80:00"},
    NekoBizDev: {low: "00:00", high: "200:00"},
    "Yodobashi Components": {low: "00:00", high: "00:00"},
    "Hippokampe (Digirail)": {low: "00:00", high: "00:00"},
    NekoRecruit: {low: "00:00", high: "20:00"},
    Koneko: {low: "50:00", high: "200:00"},
    Rhythmscapes: {low: "50:00", high: "275:00"},
    "Questy Chess": {low: "00:00", high: "50:00"},
    "Atlantis Business Support": {low: "00:00", high: "32:00"},
  },
  // July baseline — copy June until producer-adjusted figures arrive.
  "2026-07": {
    "CRAZIA: Moderation": {low: "140:00", high: "160:00"},
    Shinnyadou: {low: "00:00", high: "50:00"},
    "CRAZIA: Avatar": {low: "140:00", high: "160:00"},
    NekoAdmin: {low: "00:00", high: "150:00"},
    "Mission Catastrophic!": {low: "00:00", high: "450:00"},
    "Umami Sans Frontieres": {low: "00:00", high: "32:00"},
    "NekoLogic Website": {low: "25:00", high: "100:00"},
    AnSo: {low: "25:00", high: "125:00"},
    NekoHR: {low: "00:00", high: "75:00"},
    NekoProduction: {low: "00:00", high: "80:00"},
    NekoBizDev: {low: "00:00", high: "200:00"},
    "Yodobashi Components": {low: "00:00", high: "00:00"},
    "Hippokampe (Digirail)": {low: "00:00", high: "00:00"},
    NekoRecruit: {low: "00:00", high: "20:00"},
    Koneko: {low: "50:00", high: "200:00"},
    Rhythmscapes: {low: "50:00", high: "275:00"},
    "Questy Chess": {low: "00:00", high: "50:00"},
    "Atlantis Business Support": {low: "00:00", high: "32:00"},
  },
};

/** Hours from "H:MM" / "HH:MM" (single colon). Bare whole numbers = hours at :00. */
function parseHHMM(value) {
  if (value == null || value === "") return 0;
  const s = String(value).trim();
  const hm = s.match(/^\s*(\d+)\s*:\s*(\d{1,2})\s*$/);
  if (hm) {
    const h = parseInt(hm[1], 10);
    const m = parseInt(hm[2], 10);
    const hh = Number.isFinite(h) ? h : 0;
    const mm = Number.isFinite(m) ? Math.min(59, Math.max(0, m)) : 0;
    return hh + mm / 60;
  }
  if (/^\d+$/.test(s)) {
    return parseInt(s, 10);
  }
  const n = parseFloat(s.replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

/** Normalize project names for lookup (spacing, unicode, case-insensitive fallback). */
function normalizeBudgetLookupKey(name) {
  return String(name || "")
    .trim()
    .normalize("NFKC")
    .replace(/\s+/g, " ");
}

/**
 * Looser matching key to absorb punctuation/spacing variants:
 * e.g. "NekoHR", "Neko HR", "Neko-HR" => same key.
 */
function normalizeBudgetLooseKey(name) {
  return normalizeBudgetLookupKey(name)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

/**
 * Former NekoHR project_name values → current name.
 * Budget rows/API keys may still use the old string after a rename in Projects.
 */
export const konekoAProjectNameAliases = {
  "Cattastrophic!": "Mission Catastrophic!",
  Cattastrophic: "Mission Catastrophic!",
  "Mission Catastrophic!": "Mission Catastrophic!",
};

const STORAGE_PROJECT_NAME_ALIASES = "konekoA:projectNameAliases:v1";
let konekoAProjectNameAliasesDynamic = {};

export function mergeKonekoProjectNameAliases(fromMap) {
  if (!fromMap || typeof fromMap !== "object" || Array.isArray(fromMap)) return;
  Object.entries(fromMap).forEach(([from, to]) => {
    const oldKey = normalizeBudgetLookupKey(from);
    const newKey = normalizeBudgetLookupKey(to);
    if (oldKey && newKey && oldKey !== newKey) {
      konekoAProjectNameAliasesDynamic[oldKey] = newKey;
    }
  });
}

export function loadKonekoProjectNameAliasesFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_PROJECT_NAME_ALIASES);
    if (!raw) return;
    mergeKonekoProjectNameAliases(JSON.parse(raw));
  } catch {
    /* ignore */
  }
}

export function registerKonekoProjectRename(oldName, newName) {
  const oldKey = normalizeBudgetLookupKey(oldName);
  const newKey = normalizeBudgetLookupKey(newName);
  if (!oldKey || !newKey || oldKey === newKey) return;
  mergeKonekoProjectNameAliases({[oldKey]: newKey});
  try {
    const raw = localStorage.getItem(STORAGE_PROJECT_NAME_ALIASES);
    const map = raw ? JSON.parse(raw) : {};
    map[oldKey] = newKey;
    localStorage.setItem(STORAGE_PROJECT_NAME_ALIASES, JSON.stringify(map));
  } catch {
    /* ignore */
  }
}

/** Current display key for budget/timesheet matching after renames. */
export function canonicalizeKonekoProjectName(name) {
  const normalized = normalizeBudgetLookupKey(name);
  if (!normalized) return "";
  for (const [from, to] of Object.entries(konekoAProjectNameAliases)) {
    if (normalizeBudgetLookupKey(from) === normalized) {
      return normalizeBudgetLookupKey(to);
    }
  }
  for (const [from, to] of Object.entries(konekoAProjectNameAliasesDynamic)) {
    if (normalizeBudgetLookupKey(from) === normalized) {
      return normalizeBudgetLookupKey(to);
    }
  }
  return normalized;
}

function konekoProjectCanonKey(name) {
  return canonicalizeKonekoProjectName(name) || normalizeBudgetLookupKey(name);
}

function preferCanonicalProjectDisplayName(candidate, incumbent) {
  if (!incumbent) return candidate;
  const cCanon = konekoProjectCanonKey(candidate);
  if (normalizeBudgetLookupKey(candidate) === cCanon) return candidate;
  const iCanon = konekoProjectCanonKey(incumbent);
  if (normalizeBudgetLookupKey(incumbent) === iCanon) return incumbent;
  return candidate.length >= incumbent.length ? candidate : incumbent;
}

/** Merge renamed/legacy project keys into one row per canonical name. */
export function resolveCanonicalProjectDisplayNames(...nameLists) {
  const groups = new Map();
  for (const names of nameLists) {
    if (!Array.isArray(names)) continue;
    for (const name of names) {
      const raw = normalizeBudgetLookupKey(name);
      if (!raw) continue;
      const canon = konekoProjectCanonKey(raw);
      groups.set(
        canon,
        preferCanonicalProjectDisplayName(raw, groups.get(canon))
      );
    }
  }
  return Array.from(groups.values());
}

function orgBudgetFromApiCache(
  monthKey,
  projectName,
  orgCacheByMonth = konekoOrgBudgetsApiCacheByMonth
) {
  const byMonth = orgCacheByMonth[monthKey];
  if (!byMonth) return null;
  return findLimitsValueForProject(byMonth, projectName);
}

export function getKonekoProjectBudgetBand(
  monthKey,
  projectName,
  orgCacheByMonth = konekoOrgBudgetsApiCacheByMonth
) {
  const mk = String(monthKey || "").trim();
  const fromApi = orgBudgetFromApiCache(mk, projectName, orgCacheByMonth);
  if (fromApi) {
    if (typeof fromApi === "string") {
      return {low: "", high: fromApi, target: ""};
    }
    if (typeof fromApi === "object") return fromApi;
  }
  const byMonth = konekoAMonthlyProjectBudgets[mk];
  if (!byMonth || typeof byMonth !== "object") return null;

  const needle = normalizeBudgetLookupKey(projectName);
  if (!needle) return null;

  const keys = Object.keys(byMonth);
  const exact = keys.find((k) => normalizeBudgetLookupKey(k) === needle);
  if (exact != null) return byMonth[exact];

  const needleLower = needle.toLowerCase();
  const ci = keys.find(
    (k) => normalizeBudgetLookupKey(k).toLowerCase() === needleLower
  );
  if (ci != null) return byMonth[ci];

  const needleLoose = normalizeBudgetLooseKey(needle);
  const loose = keys.find((k) => normalizeBudgetLooseKey(k) === needleLoose);
  return loose != null ? byMonth[loose] : null;
}

/** Parsed numeric hours for budget bar UI (null = unset). */
export function bandToDecimals(band) {
  if (!band) return {low: null, high: null, target: null};
  const fin = (x) => (typeof x === "number" && Number.isFinite(x) ? x : null);
  return {
    low: band.low != null && band.low !== "" ? fin(parseHHMM(band.low)) : null,
    high:
      band.high != null && band.high !== "" ? fin(parseHHMM(band.high)) : null,
    target:
      band.target != null && band.target !== ""
        ? fin(parseHHMM(band.target))
        : null,
  };
}

/** False for missing band or 0:00–0:00 placeholder (hide idle projects). */
export function orgBudgetBandIsConfigured(band) {
  if (!band || typeof band !== "object") return false;
  const d = bandToDecimals(band);
  return (
    (d.low != null && d.low > 0) ||
    (d.high != null && d.high > 0) ||
    (d.target != null && d.target > 0)
  );
}

/**
 * @param {number} monthHoursDecimal — logged hours for the project this calendar month (aggregate)
 * @param {{ target?: string, low?: string, high?: string } | null} band
 * @returns {string|null} one line for the snapshot table, or null if no band
 */
export function formatProjectBudgetStatus(monthHoursDecimal, band) {
  if (!band) return null;
  const m = Math.max(0, monthHoursDecimal);
  const t = band.target != null ? parseHHMM(band.target) : 0;
  const lo = band.low != null ? parseHHMM(band.low) : 0;
  const hi = band.high != null ? parseHHMM(band.high) : 0;

  const pct = (num, den) => {
    if (!(den > 0)) return null;
    return Math.round((num / den) * 100);
  };

  if (t > 0 && lo <= 0 && hi <= 0) {
    const p = pct(m, t);
    return p == null ? null : `${p}% of budget`;
  }

  if (lo > 0 && hi > 0) {
    if (m <= lo) {
      const p = pct(m, lo);
      return p == null ? null : `${p}% of low end of budget`;
    }
    if (m >= hi) {
      const p = pct(m, hi);
      return p == null ? null : `${p}% of high end of budget`;
    }
    const pLo = pct(m, lo);
    const pHi = pct(m, hi);
    return `Within budget band (${pLo}% of low end · ${pHi}% of high end)`;
  }

  if (lo > 0 && hi <= 0) {
    const p = pct(m, lo);
    return p == null ? null : `${p}% of low end of budget`;
  }
  if (hi > 0 && lo <= 0) {
    const p = pct(m, hi);
    return p == null ? null : `${p}% of high end of budget`;
  }
  return null;
}

/**
 * True when logged month hours exceed the configured cap (high of band, single bound, or target-only).
 * @param {number} currentDecimal
 * @param {{ low?: number|null, high?: number|null, target?: number|null }} caps — e.g. `budgetBar` / `personalBudgetBar` without `current`
 */
/** Vuetify text classes for org/personal budget bar status. */
export function konekoBudgetStatusTextClass(status) {
  if (status === "over") return "red--text text--darken-2 font-weight-medium";
  if (status === "pace") return "amber--text text--darken-3 font-weight-medium";
  if (status === "good") return "green--text text--darken-2 font-weight-medium";
  return "";
}

export function isKonekoBudgetBarOverrun(currentDecimal, caps) {
  const m = Math.max(0, Number(currentDecimal) || 0);
  if (!caps || typeof caps !== "object") return false;
  const lo = caps.low != null && caps.low > 0 ? caps.low : null;
  const hi = caps.high != null && caps.high > 0 ? caps.high : null;
  const tgt = caps.target != null && caps.target > 0 ? caps.target : null;

  if (tgt != null && lo == null && hi == null) {
    return m > tgt;
  }
  if (lo != null && hi != null) {
    return m > Math.max(lo, hi);
  }
  if (lo != null && hi == null) {
    return m > lo;
  }
  if (hi != null && lo == null) {
    return m > hi;
  }
  return false;
}
